import React from "react";
import { Link } from "react-router-dom";
import Item from "../models/item";
import PageTitle from "../components/PageTitle";
import { useParams } from "react-router";
import { useQuery } from "../hooks/useQuery";
import NotFound from "./NotFound";
import { Helmet } from "react-helmet";
import { ModelDetails } from "../configuration/model-description";
import { Settings } from "../configuration/settings";
import RecomendedItems from "../components/RecomendedItems";
import { PhotoProvider, PhotoView } from 'react-photo-view';
import 'react-photo-view/dist/react-photo-view.css';
import Breadcrumbs from "../components/Breadcrumbs";
import { ModelCategories } from "../configuration/model-category";
import SeoCollapse from "../components/SeoCollapse";

function JeansDetails(props: { items: Item[] }) {
    let { slug } = useParams();
    let query = useQuery();
    let waist = query.get("w");
    let length = query.get("l");

    const selectedItemVariations = props.items.filter(x => x.slug == slug);
    const selectedItem = selectedItemVariations.find((x) => x.waist == waist && x.length == length) ||
        selectedItemVariations.find(x => x.stockStatus == "inStock") || 
        selectedItemVariations[0];

    if (!selectedItem) {
        return (<NotFound />)
    }

    const modelDetails = ModelDetails[selectedItem.model];
    const modelCategory = ModelCategories.find(x => x.models.includes(selectedItem.model));

    return (
        <>
            <Helmet>
                <title>{`Джинси ${selectedItem.name} ${selectedItem.color} - ${Settings.shopName}`}</title>
                <meta name="description" content={`Оригінальні чоловічі джинси ${selectedItem.name} у кольорі ${selectedItem.color}. Гарантія оригіналу, актуальні розміри та ціна. Доставка по всій Україні від магазину ${Settings.shopName}.`} />
                <link rel="canonical" href={`${Settings.siteUrl}/p/${selectedItem.slug}/`} />
                <meta property="og:title" content={`Джинси ${selectedItem.name} ${selectedItem.color} - ${Settings.shopName}`} />
                <meta property="og:description" content={`Оригінальні чоловічі джинси ${selectedItem.name} у кольорі ${selectedItem.color}. Гарантія оригіналу, актуальні розміри та ціна. Доставка по всій Україні від магазину ${Settings.shopName}.`} />
                <meta property="og:image" content={selectedItem.imageFront} />
                <script type="application/ld+json">{`
                {
                    "@context":"https://schema.org",
                    "@type":"Product",
                    "name":"Джинси ${selectedItem.name} ${selectedItem.color}",
                    "url":"${Settings.siteUrl}/p/${selectedItem.slug}/",
                    "image":["${selectedItem.imageFront}","${selectedItem.imageBack}"],
                    "description":"Оригінальні чоловічі джинси ${selectedItem.name} у кольорі ${selectedItem.color}. Гарантія оригіналу, актуальні розміри та ціна. Доставка по всій Україні від магазину ${Settings.shopName}.",
                    "sku":"${selectedItem.id}",
                    "itemCondition":"https://schema.org/NewCondition",
                    "offers":{"@type":"Offer","availability":"https://schema.org/${selectedItem.stockStatus == "outOfStock" ? "OutOfStock" : selectedItem.stockStatus == "inStock" ? "InStock" : "PreOrder"}","url":"${Settings.siteUrl}/p/${selectedItem.slug}/","price":"${selectedItem.price}","priceCurrency":"UAH","seller":{"@type":"Organization","name":"${Settings.shopName}","url":"${Settings.siteUrl}"}},
                    "brand":{"@type":"Brand","name":"Levi’s"}${selectedItem.stockStatus != "outOfStock" ? `,
                    "size":[${selectedItemVariations.map(v => "\"" + v.waist + "-" + v.length + "\"").join(",")}]` : ""}
                }`}
                </script>
            </Helmet>
            <Breadcrumbs items={[
                { text: `${selectedItem.model}`, url: `/m/levis-${selectedItem.model}/`, title: `Джинси Levi’s ${selectedItem.model}` },
                { text: `${selectedItem.color}`, title: `Джинси Levi’s ${selectedItem.model} ${selectedItem.color}`}
            ]} />
            <PageTitle title={`Джинси ${selectedItem.name} ${selectedItem.color}`} />
            <div className="row">
                <div className="col-12 col-lg-6 mb-3">
                    {selectedItem.tag &&
                        <div className="position-relative">
                            <span className="position-absolute top-0 start-0 badge rounded-pill bg-secondary m-2 ps-1 pe-1 text-uppercase fw-bold" style={{zIndex:9}}>{selectedItem.tag}</span>
                        </div>
                    }
                    <PhotoProvider maskOpacity={0.9}>
                        <div id={"carouselExampleControls" + selectedItem.id} className="carousel carousel-dark slide" data-bs-ride="carousel">
                            <div className="carousel-inner">
                                <div className="carousel-item active">
                                    <div style={{ width: "100%" }}> {/* Hook to reserve height == width during loading */}
                                        <div style={{ height: "0", paddingBottom: "100%" }}> {/* Hook to reserve height == width during loading */}
                                            <PhotoView key={`photoview-front-${selectedItem.id}`} src={selectedItem.imageFrontMaxSize || selectedItem.imageFront}>
                                                <img src={selectedItem.imageFront} className="img-fluid" alt={`Оригінальні чоловічі джинси ${selectedItem.name} у кольорі ${selectedItem.color}, вид спереду`} />
                                            </PhotoView>
                                        </div>
                                    </div>
                                </div>
                                {selectedItem.imageBack &&
                                    <div className="carousel-item">
                                        <PhotoView key={`photoview-back-${selectedItem.id}`} src={selectedItem.imageBackMaxSize || selectedItem.imageBack}>
                                            <img src={selectedItem.imageBack} className="img-fluid" alt={`Оригінальні чоловічі джинси ${selectedItem.name} у кольорі ${selectedItem.color}, вид ззаду`} loading="lazy" />
                                        </PhotoView>
                                    </div>
                                }
                                {selectedItem.imageSide &&
                                    <div className="carousel-item">
                                        <PhotoView key={`photoview-side-${selectedItem.id}`} src={selectedItem.imageSideMaxSize || selectedItem.imageSide}>
                                            <img src={selectedItem.imageSide} className="img-fluid" alt={`Оригінальні чоловічі джинси ${selectedItem.name} у кольорі ${selectedItem.color}, вид збоку`} loading="lazy" />
                                        </PhotoView>
                                    </div>
                                }
                            </div>
                            <button className="carousel-control-prev" type="button" data-bs-target={"#carouselExampleControls" + selectedItem.id} data-bs-slide="prev">
                                <span className="carousel-control-prev-icon" aria-hidden="true"></span>
                                <span className="visually-hidden">Попереднє фото</span>
                            </button>
                            <button className="carousel-control-next" type="button" data-bs-target={"#carouselExampleControls" + selectedItem.id} data-bs-slide="next">
                                <span className="carousel-control-next-icon" aria-hidden="true"></span>
                                <span className="visually-hidden">Наступне фото</span>
                            </button>
                        </div>
                    </PhotoProvider>
                </div>
                <div className="col-12 col-lg-6 text-start">
                    <p className="mb-0"><strong>Розмір виробника:</strong> {selectedItem.waist}-{selectedItem.length}</p>
                    <ul className="ps-0 mb-0">
                        {selectedItemVariations.map((size, i) => {
                            return (
                                <li className="d-inline-block">
                                    <Link
                                        className={"btn btn-outline-secondary mt-2 me-2" + (size.waist == selectedItem.waist && size.length == selectedItem.length ? " active" : "")}
                                        to={"/p/" + size.slug + "/?w=" + size.waist + "&l=" + size.length}
                                        role="button"
                                        title={`Джинси ${size.name} ${size.id} ${size.waist}-${size.length}`}
                                    >
                                        {size.waist}-{size.length}
                                    </Link>
                                </li>
                            )
                        })}
                    </ul>
                    <Link
                        className="link-secondary d-block mt-2"
                        to="/tablytsia-rozmiriv-levis/"
                        title="Таблиця розмірів джинсів Levi’s"
                    >
                        Таблиця розмірів
                    </Link>
                    <div className="border rounded mt-3">
                        <div className="p-3">
                            <div className="mb-1">
                                <span className="h2">{selectedItem.price}</span><span>₴</span>
                            </div>
                            {
                                selectedItem.stockStatus == "outOfStock" ? <p className="text-secondary mb-2">Тимчасово немає в наявності</p> :
                                selectedItem.stockStatus == "inStock" ? <p className="text-success mb-2">Є в наявності</p> :
                                <p className="text-secondary mb-2">Очікується поставка</p>
                            }
                            <Link
                                className={"btn w-100 " + (selectedItem.stockStatus == "inStock" ? "btn-success" : "btn-outline-secondary disabled")}
                                to={"/order/?id=" + selectedItem.id + "&w=" + selectedItem.waist + "&l=" + selectedItem.length}
                                role="button"
                                title="Купити"
                            >
                                Купити
                            </Link>
                        </div>
                        <div className="border-top">
                            <p className="px-3 pt-2">
                                <Link className="link-secondary" to="/delivery-and-payment/">
                                    <strong>Доставка і оплата по Україні</strong> - детальні умови
                                </Link>
                            </p>
                        </div>
                    </div>
                    <h2 className="fs-5 mt-3">Характеристики</h2>
                    <ul>
                        {modelDetails && modelDetails.features.map(feature =>
                            (<li>{feature}</li>)
                        )}
                        <li>Колір: {selectedItem.color}</li>
                        <li>Артикул: {selectedItem.id}</li>
                        {selectedItem.composition && <li>Склад: {selectedItem.composition}</li>}
                        <li>Бренд: <Link className="link-secondary text-decoration-none" to="/"><strong>Levi’s</strong></Link></li>
                    </ul>
                    {selectedItem.halfWaistband && selectedItem.rise && selectedItem.seam && selectedItem.legOpening &&
                        <>
                            <h2 className="fs-5">Заміри</h2>
                            <Link
                                className="link-secondary"
                                to="/yak-robyty-zamiry-dzhyns-levis/"
                                title="Як правильно робити заміри джинсів Levi’s?"
                            >
                                Як робити заміри джинсів?
                            </Link>
                            <ul>
                                <li>Напівпояс: {selectedItem.halfWaistband} см</li>
                                <li>Посадка: {selectedItem.rise} см</li>
                                <li>Довжина: {selectedItem.seam} см</li>
                                <li>Штанина низ: {selectedItem.legOpening} см</li>
                            </ul>
                        </>
                    }
                </div>
            </div>
            {modelDetails &&
                <div>
                    <h2 className="fs-5">Опис</h2>
                    <SeoCollapse>
                        <div dangerouslySetInnerHTML={{ __html: modelDetails.description.replaceAll("{{color}}", selectedItem.color) }} />
                    </SeoCollapse>
                </div>
            }
            <RecomendedItems
                items={props.items.filter(x => x.model == selectedItem.model)}
                exceptIds={[selectedItem.id]}
                waist={selectedItem.waist}
                length={selectedItem.length}
                title="Інші варіанти цієї моделі Levi’s"
            />
            <div className="border-top border-end border-bottom">
                <p className="ps-3 pt-2 text-center">
                    <Link className="link-secondary" to={`/m/levis-${selectedItem.model}/`}>
                        Усі варіанти моделі <strong>Levi’s {selectedItem.model}</strong>
                    </Link>
                </p>
            </div>
            <RecomendedItems
                items={props.items.filter(x => x.model != selectedItem.model && modelCategory?.models.includes(x.model))}
                exceptIds={[selectedItem.id]}
                waist={selectedItem.waist}
                length={selectedItem.length}
                title="Схожі за кроєм джинси Levi’s"
            />
        </>
    );
}

export default JeansDetails;
